/**
 * Tax Expense Tracker - Cloud API Service
 * Connects to Cloudflare Workers backend
 */

import { Expense, DriverSettings, Stats, DEFAULT_SETTINGS } from '../types';

// API base URL - must point to Worker; never use relative /api (Pages returns HTML)
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  'https://tax-expense-tracker-api.mr-doug-sullivan.workers.dev';

// Hard fail if pointing at Pages (would return HTML, not JSON)
if (API_BASE_URL.includes('pages.dev')) {
  throw new Error(
    'API_BASE_URL must point to Worker, not Pages. Set VITE_API_BASE_URL=https://tax-expense-tracker-api.mr-doug-sullivan.workers.dev'
  );
}
if (typeof window !== 'undefined' && API_BASE_URL === window.location.origin) {
  throw new Error(
    'API_BASE_URL must point to Worker, not same origin. Set VITE_API_BASE_URL=https://tax-expense-tracker-api.mr-doug-sullivan.workers.dev'
  );
}
if (import.meta.env.DEV) {
  console.log('API_BASE_URL', API_BASE_URL);
}

/** Parse response as JSON; if not JSON, throw with body preview. Never use res.json() on API responses. */
export async function parseJsonResponse<T>(res: Response): Promise<T | undefined> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const ct = res.headers.get('content-type') || '';
  const url = res.url || '(unknown)';
  const status = res.status;
  const errPrefix = `Non-JSON response from ${url} (${status})`;
  const preview = text.slice(0, 200);

  // Reject HTML or non-JSON before parse
  if (text.trim().startsWith('<')) {
    console.error('Response content-type:', ct, 'Raw preview:', preview);
    throw new Error(`${errPrefix} - HTML body: ${preview}`);
  }
  if (!text.trim()) return undefined as T;
  if (!ct.includes('application/json') && !text.trimStart().startsWith('{') && !text.trimStart().startsWith('[')) {
    console.error('Response content-type:', ct, 'Raw preview:', preview);
    throw new Error(`${errPrefix} - expected JSON, got ${ct || 'unknown'}: ${preview}`);
  }
  try {
    return JSON.parse(text) as T;
  } catch (e) {
    throw new Error(`${errPrefix} - ${(e as Error).message} - body: ${preview}`);
  }
}

// Helper for API requests. Adds Authorization when token present. No profile scoping (legacy global data).
async function apiRequest<T>(
  endpoint: string, 
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errData = await parseJsonResponse<{ error?: string }>(response).catch((e) => ({
      error: e instanceof Error ? e.message : 'Request failed',
    }));
    throw new Error(errData?.error || `API error: ${response.status}`);
  }

  return parseJsonResponse<T>(response);
}

// =====================================================
// EXPENSE OPERATIONS
// =====================================================

/** Distinct years from expenses table (for Tax Year dropdown). Global data – no user filter. */
export async function getExpenseYears(): Promise<number[]> {
  try {
    const data = await apiRequest<number[]>('/api/expenses/years');
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Failed to fetch expense years:', error);
    return [];
  }
}

/** Fetch expenses. Mode optional (omit for global data pool). */
export async function getAllExpenses(
  source?: 'manual' | 'gmail' | 'paste' | 'import',
  mode?: 'business' | 'family'
): Promise<Expense[]> {
  try {
    const params = new URLSearchParams();
    if (source) params.set('source', source);
    if (mode) params.set('mode', mode);
    const url = `${API_BASE_URL}/api/expenses${params.toString() ? '?' + params : ''}`;
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const errData = await parseJsonResponse<{ error?: string }>(response).catch(() => ({}));
      throw new Error(errData?.error || `API error: ${response.status}`);
    }
    const expenses = await parseJsonResponse<any[]>(response) ?? [];
    return expenses.map(mapCloudExpenseToLocal);
  } catch (error) {
    console.error('Failed to fetch expenses:', error);
    throw error;
  }
}

export async function getExpense(id: string): Promise<Expense | undefined> {
  try {
    const expense = await apiRequest<any>(`/api/expenses/${id}`);
    return mapCloudExpenseToLocal(expense);
  } catch (error) {
    console.error('Failed to fetch expense:', error);
    return undefined;
  }
}

export async function addExpense(expense: Expense): Promise<void> {
  try {
    await apiRequest('/api/expenses', {
      method: 'POST',
      body: JSON.stringify({
        id: expense.id,
        category: expense.category,
        subcategory: expense.subcategory,
        description: expense.description,
        amount: expense.amount,
        date: expense.date,
        month: expense.month,
        year: expense.year,
        filename: expense.filename,
        fileUrl: expense.fileUrl,  // R2 key from /api/upload - NOT base64/parsed text
        fileType: expense.fileType,
        fileSize: expense.fileSize,
        freightBillNumber: expense.freightBillNumber,
        isSentToBennett: expense.isSentToBennett,
        source: expense.provenanceSource ?? 'manual',
        sourceRef: expense.sourceRef,
        mode: (expense as Expense & { mode?: string }).mode ?? 'business',
      }),
    });
  } catch (error) {
    console.error('Failed to add expense:', error);
    throw error;
  }
}

export async function updateExpense(expense: Expense): Promise<void> {
  try {
    await apiRequest(`/api/expenses/${expense.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        category: expense.category,
        subcategory: expense.subcategory,
        description: expense.description,
        amount: expense.amount,
        date: expense.date,
        month: expense.month,
        year: expense.year,
        filename: expense.filename,
        fileUrl: expense.fileUrl,
        fileType: expense.fileType,
        fileSize: expense.fileSize,
        freightBillNumber: expense.freightBillNumber,
        isSentToBennett: expense.isSentToBennett
      }),
    });
  } catch (error) {
    console.error('Failed to update expense:', error);
    throw error;
  }
}

export async function deleteExpense(id: string): Promise<void> {
  try {
    await apiRequest(`/api/expenses/${id}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error('Failed to delete expense:', error);
    throw error;
  }
}

export async function markBOLAsSent(id: string): Promise<void> {
  try {
    await apiRequest(`/api/expenses/${id}/sent-to-bennett`, {
      method: 'PATCH',
    });
  } catch (error) {
    console.error('Failed to mark BOL as sent:', error);
    throw error;
  }
}

export async function getExpensesByCategory(category: string, mode: 'business' | 'family'): Promise<Expense[]> {
  try {
    const expenses = await apiRequest<any[]>(`/api/expenses/category/${encodeURIComponent(category)}?mode=${encodeURIComponent(mode)}`);
    return expenses.map(mapCloudExpenseToLocal);
  } catch (error) {
    console.error('Failed to fetch expenses by category:', error);
    throw error;
  }
}

export async function getExpensesByMonthYear(year: number, month: number, mode: 'business' | 'family'): Promise<Expense[]> {
  try {
    const expenses = await apiRequest<any[]>(`/api/expenses/month/${year}/${month}?mode=${encodeURIComponent(mode)}`);
    return expenses.map(mapCloudExpenseToLocal);
  } catch (error) {
    console.error('Failed to fetch expenses by month:', error);
    throw error;
  }
}

// =====================================================
// SETTINGS OPERATIONS
// =====================================================

export async function getSettings(): Promise<DriverSettings> {
  try {
    const settings = await apiRequest<DriverSettings>('/api/settings');
    return settings;
  } catch (error) {
    console.error('Failed to fetch settings:', error);
    return DEFAULT_SETTINGS;
  }
}

export async function saveSettings(settings: DriverSettings): Promise<void> {
  try {
    await apiRequest('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  } catch (error) {
    console.error('Failed to save settings:', error);
    throw error;
  }
}

// =====================================================
// STATS OPERATIONS
// =====================================================

/** Fetch stats. Mode optional (omit for global data pool). */
export async function getStats(mode?: 'business' | 'family'): Promise<Stats> {
  try {
    const q = mode ? `?mode=${encodeURIComponent(mode)}` : '';
    const url = `${API_BASE_URL}/api/stats${q}`;
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const errData = await parseJsonResponse<{ error?: string }>(response).catch(() => ({}));
      throw new Error(errData?.error || `API error: ${response.status}`);
    }
    return (await parseJsonResponse<Stats>(response))!;
  } catch (error) {
    console.error('Failed to fetch stats:', error);
    throw error;
  }
}

export async function calculateStorageUsed(mode?: 'business' | 'family'): Promise<{ bytes: number; formatted: string }> {
  try {
    const stats = await getStats(mode);
    return {
      bytes: stats.storageUsed,
      formatted: stats.storageUsedFormatted,
    };
  } catch (error) {
    console.error('Failed to calculate storage:', error);
    return { bytes: 0, formatted: '0 KB' };
  }
}

// =====================================================
// FILE OPERATIONS
// =====================================================

// =====================================================
// BULK UPLOAD / BINDER OPERATIONS
// =====================================================

export interface UploadRecord {
  id: string;
  created_at: string;
  original_name: string;
  mime_type: string;
  byte_size: number;
  sha256: string | null;
  r2_key: string;
  source: string;
  status: string;
  parse_json: string | null;
  error: string | null;
}

/** Bulk upload to storage (uploads table) - for binder */
export async function bulkUploadToStorage(files: File[]): Promise<{ ok: boolean; count: number; items: Array<{ id: string; original_name: string; r2_key: string; sha256: string; size: number; mime_type: string }> }> {
  const url = `${API_BASE_URL}/api/upload/bulk`;
  const formData = new FormData();
  for (const file of files) formData.append('files', file);
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { method: 'POST', headers, body: formData });
  const data = await parseJsonResponse<{ ok?: boolean; error?: string; count?: number; items?: unknown[] }>(response);
  if (!response.ok) throw new Error(data.error || 'Bulk upload failed');
  return data as { ok: boolean; count: number; items: Array<{ id: string; original_name: string; r2_key: string; sha256: string; size: number; mime_type: string }> };
}

/** List uploads */
export async function listUploads(limit = 50, status?: string): Promise<UploadRecord[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (status) params.set('status', status);
  const url = `${API_BASE_URL}/api/uploads?${params}`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { headers });
  if (!response.ok) throw new Error('Failed to list uploads');
  return parseJsonResponse<UploadRecord[]>(response);
}


/** Download upload as blob (with auth header) */
export async function downloadUpload(id: string): Promise<Blob> {
  const url = `${API_BASE_URL}/api/uploads/${id}/download`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { headers });
  if (!response.ok) throw new Error('Download failed');
  return response.blob();
}

/** Get PDF text preview for an upload (diagnostics) */
export async function getUploadTextPreview(id: string): Promise<{
  ok: boolean;
  upload: { id: string; original_name: string; mime_type: string; byte_size: number; status: string };
  textPreview: string;
  charCount: number;
  isEmpty: boolean;
}> {
  const url = `${API_BASE_URL}/api/uploads/${id}/text-preview`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { headers });
  const data = await parseJsonResponse<{ ok?: boolean; error?: string; upload?: object; textPreview?: string; charCount?: number; isEmpty?: boolean }>(response);
  if (!response.ok) throw new Error((data as { error?: string })?.error || 'Text preview failed');
  return data as { ok: boolean; upload: object; textPreview: string; charCount: number; isEmpty: boolean };
}

/** Parse single upload */
export async function parseUpload(id: string): Promise<{ ok: boolean; status: string; parse_json: unknown }> {
  const url = `${API_BASE_URL}/api/uploads/${id}/parse`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { method: 'POST', headers });
  const data = await parseJsonResponse<{ ok?: boolean; error?: string; status?: string; parse_json?: unknown }>(response);
  if (!response.ok) throw new Error(data.error || 'Parse failed');
  return data as { ok: boolean; status: string; parse_json: unknown };
}

/** Bulk parse stored uploads - empty ids = parse all stored */
export async function bulkParseUploads(ids?: string[]): Promise<{ ok: boolean; parsed: number; errors: number }> {
  const url = `${API_BASE_URL}/api/uploads/parse-bulk`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(ids && ids.length > 0 ? { ids } : {}),
  });
  const data = await parseJsonResponse<{ ok?: boolean; error?: string; parsed?: number; errors?: number }>(response);
  if (!response.ok) throw new Error(data?.error || 'Bulk parse failed');
  return (data ?? { ok: false, parsed: 0, errors: 0 }) as { ok: boolean; parsed: number; errors: number };
}

/** List Gmail documents (for Imported from Gmail panel) */
export async function getGmailDocuments(
  source = 'gmail',
  label = '.Rates',
  limit = 20,
  mode?: 'business' | 'family'
): Promise<Array<{
  id: string;
  filename?: string;
  created_at?: string;
  status: string;
  error?: string | null;
  parse_json?: string | null;
  draft_json?: string | null;
  received_at?: string | null;
  mode?: string | null;
}>> {
  const params = new URLSearchParams({ source, label, limit: String(limit) });
  if (mode) params.set('mode', mode);
  const data = await apiRequest<unknown[]>(`/api/documents?${params}`);
  return data as Array<{
    id: string;
    filename?: string;
    created_at?: string;
    status: string;
    error?: string | null;
    parse_json?: string | null;
    draft_json?: string | null;
    received_at?: string | null;
    mode?: string | null;
  }>;
}

export async function updateDocumentMode(documentId: string, mode: 'business' | 'family'): Promise<{ id: string; mode: string }> {
  return apiRequest(`/api/documents/${documentId}`, {
    method: 'PATCH',
    body: JSON.stringify({ mode }),
  }) as Promise<{ id: string; mode: string }>;
}

/** Get document detail (includes parse_json for Review modal textPreview) */
export async function getDocument(id: string): Promise<{
  id: string;
  filename?: string;
  status: string;
  error?: string | null;
  parse_json?: string | null;
  draft_json?: string | null;
  received_at?: string | null;
  fileBacked?: boolean;
  [key: string]: unknown;
}> {
  return apiRequest<any>(`/api/documents/${id}`);
}

/** Delete document (and any linked expenses). Used to dismiss from Import queue. */
export async function deleteDocument(id: string): Promise<{ ok: boolean }> {
  return apiRequest(`/api/documents/${id}`, { method: 'DELETE' }) as Promise<{ ok: boolean }>;
}

/** List documents with status needs_verification (for bulk auto-process). */
export async function getDocumentsNeedsVerification(limit = 2000): Promise<Array<{ id: string; filename?: string; status: string }>> {
  const data = await apiRequest<unknown[]>(`/api/documents?status=needs_verification&limit=${limit}`);
  return data as Array<{ id: string; filename?: string; status: string }>;
}

/** Bulk auto-process documents: Vision OCR → LLM → expense. Returns processed IDs and failed list. */
export async function autoProcessDocuments(documentIds: string[]): Promise<{ processed: string[]; failed: Array<{ id: string; error: string }> }> {
  const data = await apiRequest<{ processed: string[]; failed: Array<{ id: string; error: string }> }>(
    '/api/documents/auto-process',
    { method: 'POST', body: JSON.stringify({ documentIds }) }
  );
  return data ?? { processed: [], failed: [] };
}

/** Verify and commit Vision data for a document: create expense, set document to linked, clear draft_json. Uses POST /api/documents/:id/verify (not /confirm-vision). */
export async function verifyDocument(
  id: string,
  confirmedData: { amount: number; date: string; carrier?: string }
): Promise<{ ok: boolean; expenseId: string }> {
  const url = `${API_BASE_URL}/api/documents/${id}/verify`;
  console.log('[FRONTEND_DEBUG] verifyDocument URL:', url, 'payload:', confirmedData);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(localStorage.getItem('auth_token') ? { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } : {}),
    },
    body: JSON.stringify(confirmedData),
  });
  const data = await parseJsonResponse<{ ok?: boolean; expenseId?: string; error?: string }>(response);
  if (!response.ok) throw new Error((data as { error?: string })?.error || 'Verify failed');
  return { ok: !!data?.ok, expenseId: (data as { expenseId?: string }).expenseId ?? '' };
}

/** Re-run parsing for a document (Attempt Parse). */
export async function attemptParseDocument(id: string): Promise<{
  ok: boolean;
  inserted?: number;
  updated?: number;
  duplicatesSkipped?: number;
  error?: string;
}> {
  const response = await fetch(`${API_BASE_URL}/api/documents/${id}/attempt-parse`, {
    method: 'POST',
    headers: { ...(localStorage.getItem('auth_token') ? { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } : {}) },
  });
  const data = await parseJsonResponse<{ ok?: boolean; inserted?: number; updated?: number; duplicatesSkipped?: number; error?: string }>(response);
  if (!response.ok) throw new Error((data as { error?: string })?.error || 'Attempt parse failed');
  return data as { ok: boolean; inserted?: number; updated?: number; duplicatesSkipped?: number; error?: string };
}

/** Upload an OCR'd replacement PDF for a Gmail document and re-run parsing. */
export async function uploadDocumentOcr(
  id: string,
  file: File
): Promise<{ ok: boolean; inserted?: number; updated?: number; duplicatesSkipped?: number; error?: string | null }> {
  const url = `${API_BASE_URL}/api/documents/${id}/ocr-upload`;
  const formData = new FormData();
  formData.append('file', file);
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });
  const data = await parseJsonResponse<{ ok?: boolean; inserted?: number; updated?: number; duplicatesSkipped?: number; error?: string | null }>(response);
  if (!response.ok) throw new Error((data as { error?: string })?.error || 'OCR upload failed');
  return (data ?? { ok: false, error: 'Unknown error' }) as {
    ok: boolean;
    inserted?: number;
    updated?: number;
    duplicatesSkipped?: number;
    error?: string | null;
  };
}

/** Create manual settlement from parse_error document (Needs Review) */
export async function createManualSettlement(body: {
  documentId: string;
  date: string;
  amount: number;
  kind?: 'income' | 'expense';
  notes?: string;
}): Promise<unknown> {
  const response = await fetch(`${API_BASE_URL}/api/settlements/manual`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('auth_token') ? { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } : {}) },
    body: JSON.stringify(body),
  });
  const data = await parseJsonResponse<unknown>(response);
  if (!response.ok) throw new Error((data as { error?: string })?.error || 'Save failed');
  return data;
}

/** Smart Extract: context-aware AI extraction (vendor, date, amount) using historical D1 data. */
export type SmartExtractResult = {
  vendor: string | null;
  date: string | null;
  amount: number | null;
  isPotentialDuplicate: boolean;
  matchedHistoricalVendor: string | null;
};

export async function smartExtract(
  file: Blob,
  filename: string,
  category: string,
  year: number
): Promise<SmartExtractResult> {
  const form = new FormData();
  form.append('file', file, filename || 'document.pdf');
  form.append('category', category);
  form.append('year', String(year));
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${API_BASE_URL}/api/smart-extract`, {
    method: 'POST',
    headers,
    body: form,
  });
  const data = await parseJsonResponse<SmartExtractResult & { error?: string }>(response);
  if (!response.ok) throw new Error(data?.error || `Smart extract failed: ${response.status}`);
  return data as SmartExtractResult;
}

export type ReceiptExtractResult = {
  vendor: string | null;
  date: string | null;
  amount: number | null;
  category: string | null;
  subcategory: string | null;
  gallons: number | null;
  confidence: number | null;
  flag_for_review: boolean;
  flag_reason: string | null;
};

export async function receiptExtract(file: Blob, filename: string): Promise<ReceiptExtractResult> {
  const form = new FormData();
  const f = file instanceof File ? file : new File([file], filename, { type: file.type || 'image/png' });
  form.append('file', f);
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${API_BASE_URL}/api/receipt-extract`, {
    method: 'POST',
    headers,
    body: form,
  });
  const data = await parseJsonResponse<ReceiptExtractResult & { error?: string }>(response);
  if (!response.ok) throw new Error(data?.error || `Receipt extract failed: ${response.status}`);
  return data as ReceiptExtractResult;
}

/** Create manual travel expense from parse_error document (Needs Review) */
export async function createManualTravel(body: {
  documentId: string;
  date: string;
  amount: number;
  subcategory?: string;
  notes?: string;
}): Promise<unknown> {
  const response = await fetch(`${API_BASE_URL}/api/travel/manual`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('auth_token') ? { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } : {}) },
    body: JSON.stringify(body),
  });
  const data = await parseJsonResponse<unknown>(response);
  if (!response.ok) throw new Error((data as { error?: string })?.error || 'Save failed');
  return data;
}

/** Sync options for Gmail .Rates sync */
export interface SyncGmailSettlementsOptions {
  carrier?: 'All' | 'Bennett' | 'Quality Driveaway' | 'Auto Driveaway';
  days?: number;
  label?: string;
  startDate?: string;
  endDate?: string;
}

/** Admin: purge documents with error duplicate_settlement so re-sync can re-ingest. */
export async function purgeDuplicateSettlements(): Promise<{ ok: boolean; deleted: number }> {
  const url = `${API_BASE_URL}/api/admin/purge-duplicate-settlements`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { headers });
  const data = await parseJsonResponse<{ ok?: boolean; deleted?: number; error?: string }>(response);
  if (!response.ok) throw new Error((data as { error?: string })?.error || 'Purge failed');
  return { ok: !!data?.ok, deleted: (data as { deleted?: number }).deleted ?? 0 };
}

/** Sync Gmail (.Rates) for settlement PDFs – imports new settlements, returns summary */
export async function syncGmailSettlements(options: SyncGmailSettlementsOptions = {}): Promise<{
  ok: boolean;
  importedCount: number;
  skippedCount: number;
  errorCount: number;
  errors: string[];
}> {
  const url = `${API_BASE_URL}/api/import/gmail/sync`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const body: { carrier?: string; days?: number; label?: string; maxMessages?: number; startDate?: string; endDate?: string } = {
    carrier: options.carrier ?? 'All',
    days: options.days ?? 30,
    label: options.label ?? '.Rates',
    maxMessages: 20,
  };
  if (options.startDate) body.startDate = options.startDate;
  if (options.endDate) body.endDate = options.endDate;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await parseJsonResponse<{
    ok?: boolean;
    error?: string;
    importedCount?: number;
    skippedCount?: number;
    errorCount?: number;
    errors?: string[];
  }>(response);
  if (!response.ok) {
    throw new Error((data as { error?: string })?.error || 'Gmail sync failed');
  }
  return data as {
    ok: boolean;
    importedCount: number;
    skippedCount: number;
    errorCount: number;
    errors: string[];
  };
}

/** Reset .Travel documents in DB so next sync treats all as new (force re-sync). */
export async function resetTravelSync(): Promise<{ ok: boolean; deleted: number }> {
  const data = await apiRequest<{ ok?: boolean; deleted?: number }>('/api/admin/reset-travel-sync');
  return { ok: !!data?.ok, deleted: (data as { deleted?: number }).deleted ?? 0 };
}

/** Sync Gmail (.Travel) for travel receipts – imports Uber/Lyft/National, returns summary */
export async function syncGmailTravel(): Promise<{
  ok: boolean;
  importedCount: number;
  skippedCount: number;
  errorCount: number;
  errors: string[];
}> {
  const url = `${API_BASE_URL}/api/import/gmail/sync`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ label: '.Travel', maxMessages: 20 }),
  });
  const data = await parseJsonResponse<{
    ok?: boolean;
    error?: string;
    importedCount?: number;
    skippedCount?: number;
    errorCount?: number;
    errors?: string[];
  }>(response);
  if (!response.ok) {
    throw new Error((data as { error?: string })?.error || 'Gmail sync failed');
  }
  return data as {
    ok: boolean;
    importedCount: number;
    skippedCount: number;
    errorCount: number;
    errors: string[];
  };
}

/** Gmail sync - pull Gmail label, store attachments, run parsers */
export async function gmailSync(
  label = '.Rates',
  days = 90,
  maxMessages?: number
): Promise<{
  ok: boolean;
  scannedMessages: number;
  storedAttachments: number;
  processed: number;
  skippedDuplicates: number;
  errors: string[];
}> {
  const url = `${API_BASE_URL}/api/gmail/sync`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ label, days, maxMessages }),
  });
  const data = await parseJsonResponse<{
    ok?: boolean;
    error?: string;
    scannedMessages?: number;
    storedAttachments?: number;
    processed?: number;
    importedCount?: number;
    skippedDuplicates?: number;
    errors?: string[];
  }>(response);
  if (!response.ok) throw new Error((data as { error?: string })?.error || 'Gmail sync failed');
  return data as {
    ok: boolean;
    scannedMessages: number;
    storedAttachments: number;
    processed: number;
    importedCount?: number;
    skippedDuplicates: number;
    errors: string[];
  };
}

// =====================================================
// BOL & LOADBOARD
// =====================================================

export async function getBOLLatest(limit = 2): Promise<{ ok: boolean; bols: Array<{
  id: string; filename: string; date: string; documentId?: string; fileUrl?: string; downloadUrl?: string; fileBacked: boolean;
  bol?: { to_name?: string; to_city?: string; to_state?: string; to_zip?: string; raw_to_block?: string };
}> }> {
  const data = await apiRequest<{ ok: boolean; bols: unknown[] }>(`/api/bol/latest?limit=${limit}`);
  return data as { ok: boolean; bols: Array<{
    id: string; filename: string; date: string; documentId?: string; fileUrl?: string; downloadUrl?: string; fileBacked: boolean;
    bol?: { to_name?: string; to_city?: string; to_state?: string; to_zip?: string; raw_to_block?: string };
  }> };
}

export async function getLoadboardLastKnownLocation(cacheBust?: number): Promise<{ ok: boolean; city?: string; state?: string }> {
  const endpoint = cacheBust
    ? `/api/loadboard/last-known-location?ts=${cacheBust}`
    : '/api/loadboard/last-known-location';
  const data = await apiRequest<{ ok: boolean; city?: string; state?: string }>(endpoint);
  return data;
}

export async function getLoadboardRuns(
  limit = 10,
  cacheBust?: number
): Promise<{ ok: boolean; runs: Array<{
  id: string; documentId: string; downloadUrl?: string; receivedAt?: string; createdAt: string; filename?: string; r2Key?: string;
}> }> {
  const endpoint = cacheBust
    ? `/api/loadboard/runs?limit=${limit}&ts=${cacheBust}`
    : `/api/loadboard/runs?limit=${limit}`;
  const data = await apiRequest<{ ok: boolean; runs: unknown[] }>(endpoint);
  return data as { ok: boolean; runs: Array<{
    id: string; documentId: string; downloadUrl?: string; receivedAt?: string; createdAt: string; filename?: string; r2Key?: string;
  }> };
}

export async function getLoadboardRunItems(
  runId: string,
  cacheBust?: number
): Promise<{ ok: boolean; items: Array<{
  id: string; unit_type?: string; pickup_city?: string; pickup_state?: string; drop_city?: string; drop_state?: string;
  miles?: number; notes?: string; raw_row?: string;
}> }> {
  const endpoint = cacheBust
    ? `/api/loadboard/runs/${runId}/items?ts=${cacheBust}`
    : `/api/loadboard/runs/${runId}/items`;
  const data = await apiRequest<{ ok: boolean; items: unknown[] }>(endpoint);
  return data as { ok: boolean; items: Array<{
    id: string; unit_type?: string; pickup_city?: string; pickup_state?: string; drop_city?: string; drop_state?: string;
    miles?: number; notes?: string; raw_row?: string;
  }> };
}

export async function getLoadboardRecommendations(
  limit = 10,
  near?: { city: string; state: string },
  cacheBust?: number
): Promise<{
  ok: boolean; last_known_location?: { city?: string; state?: string }; destinations: Array<{ to_city?: string; to_state?: string }>;
  recommendations: Array<{
    id: string; run_id: string; unit_type?: string; pickup_city?: string; pickup_state?: string;
    drop_city?: string; drop_state?: string; miles?: number; notes?: string; raw_row?: string; reason: string; distance_miles?: number;
  }>; message?: string;
}> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (near?.city) params.set('near_city', near.city);
  if (near?.state) params.set('near_state', near.state);
  if (cacheBust) params.set('ts', String(cacheBust));
  const data = await apiRequest<{
    ok: boolean; last_known_location?: { city?: string; state?: string }; destinations: unknown[]; recommendations: unknown[]; message?: string;
  }>(`/api/loadboard/recommendations?${params}`);
  return data as {
    ok: boolean; last_known_location?: { city?: string; state?: string }; destinations: Array<{ to_city?: string; to_state?: string }>;
    recommendations: Array<{
      id: string; run_id: string; unit_type?: string; pickup_city?: string; pickup_state?: string;
      drop_city?: string; drop_state?: string; miles?: number; notes?: string; raw_row?: string; reason: string; distance_miles?: number;
    }>; message?: string;
  };
}

/** Get Google OAuth start URL - uses Workers domain (API_BASE_URL), never Pages */
export function getGoogleAuthStartUrl(): string {
  return `${API_BASE_URL}/api/auth/google/start`;
}

/** Export binder as ZIP */
export async function exportBinder(ids: string[], format: 'zip' | 'pdfzip' = 'zip', includeManifest = true): Promise<Blob> {
  const url = `${API_BASE_URL}/api/binder/export`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ids, format, include_manifest: includeManifest }),
  });
  if (!response.ok) {
    const err = await parseJsonResponse<{ error?: string }>(response).catch(() => ({}));
    throw new Error((err as { error?: string }).error || `Export failed: ${response.status}`);
  }
  return response.blob();
}

/** Upload files to storage then process by category (canonical pipeline) */
export async function uploadAndProcess(
  category: string,
  files: File[],
  targetYear?: number | 'auto'
): Promise<{ uploadResult: { ok: boolean; count: number; items: Array<{ id: string; original_name: string }> }; processResult: { ok: boolean; count: number; items: Array<{ id: string; status: string; errors?: string; hint?: string }> } }> {
  const uploadResult = await bulkUploadToStorage(files);
  if (uploadResult.count === 0) {
    return { uploadResult, processResult: { ok: true, count: 0, items: [] } };
  }
  const ids = uploadResult.items.map((i) => i.id);
  const processResult = await processFromUploads(ids, category, targetYear);
  return { uploadResult, processResult };
}

/** Process upload IDs by category (settlements, travel, fuel, etc.) */
export async function processFromUploads(
  ids: string[],
  category: string,
  targetYear?: number | 'auto'
): Promise<{ ok: boolean; count: number; items: Array<{ id: string; status: string; errors?: string; hint?: string }> }> {
  const url = `${API_BASE_URL}/api/import/process-from-uploads`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ids, category, targetYear }),
  });
  const data = await parseJsonResponse<{ ok?: boolean; error?: string; count?: number; items?: Array<{ id: string; status: string; errors?: string; hint?: string }> }>(response);
  if (!response.ok) throw new Error(data.error || `Process failed: ${response.status}`);
  return data as { ok: boolean; count: number; items: Array<{ id: string; status: string; errors?: string; hint?: string }> };
}

/** Upload receipt file to R2 via multipart/form-data. Returns key, contentType, size. */
export async function uploadReceiptFile(file: File | Blob, filename: string): Promise<{ key: string; contentType: string; size: number }> {
  const url = `${API_BASE_URL}/api/upload`;
  const formData = new FormData();
  // Blob/File - use File if we have filename for multipart
  const fileToSend = file instanceof File ? file : new File([file], filename, { type: file.type });
  formData.append('file', fileToSend);

  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Do NOT set Content-Type - browser sets multipart boundary automatically

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  const data = await parseJsonResponse<{ ok?: boolean; key?: string; contentType?: string; size?: number; error?: string }>(response);
  if (!response.ok) {
    throw new Error((data as { error?: string })?.error || `Upload failed: ${response.status}`);
  }
  const d = data as { ok?: boolean; key?: string; contentType?: string; size?: number; error?: string };
  if (!d?.ok || !d?.key) {
    throw new Error(d?.error || 'Upload failed');
  }
  return { key: d.key, contentType: d.contentType || '', size: d.size || 0 };
}

export function getFileUrl(fileUrl: string): string {
  if (!fileUrl) return '';
  // If it's already a full URL, return as-is
  if (fileUrl.startsWith('http')) return fileUrl;
  // If it's a data URL, return as-is
  if (fileUrl.startsWith('data:')) return fileUrl;
  // Otherwise, construct the API URL
  const key = fileUrl.replace(/^documents\//, '');
  return `${API_BASE_URL}/api/files/${key}`;
}

/** Fetch file from R2 by key (fileUrl) with auth; returns blob for preview (iframe/img). Caller must revoke the returned object URL when done. */
export async function fetchFileBlobByKey(r2Key: string): Promise<Blob> {
  if (!r2Key) throw new Error('fileUrl required');
  const key = r2Key.replace(/^documents\//, '');
  const url = `${API_BASE_URL}/api/files/${encodeURIComponent(key)}`;
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { headers });
  if (!response.ok) throw new Error(`File fetch failed: ${response.status}`);
  return response.blob();
}

export type AuditPendingItem = {
  id: string;
  date: string;
  month: number;
  year: number;
  category: string;
  subcategory?: string | null;
  description: string;
  amount?: number | null;
  filename: string;
  fileUrl?: string | null;
  fileType?: string;
  confidence_score?: number | null;
  is_flagged_duplicate?: number | null;
  category_locked?: number | null;
  vendor_name?: string | null;
  card_last4?: string | null;
  mode?: string | null;
  virtual_path?: string | null;
  source?: string | null;
};

export type AuditPendingResponse = {
  pending: AuditPendingItem[];
  fingerprinted: AuditPendingItem[];
};

export async function getAuditPending(): Promise<AuditPendingResponse> {
  return apiRequest<AuditPendingResponse>('/api/admin/audit/pending');
}

export async function approveAuditSelection(ids: string[]): Promise<{ ok: boolean; updated: number }> {
  return apiRequest<{ ok: boolean; updated: number }>('/api/admin/audit/approve', {
    method: 'PATCH',
    body: JSON.stringify({ ids }),
  });
}

// =====================================================
// W2 RECONCILIATION & INCOME AUDIT
// =====================================================

export type W2Record = {
  id: string;
  year: number;
  wages_box1: number;
  tax_withheld_box2: number;
  is_statutory_employee: number;
  employer_name?: string | null;
  r2_key?: string | null;
  createdAt?: string;
};

export type SettlementDetailItem = {
  id: string;
  date: string;
  month: number;
  amount: number;
  vendor_name: string | null;
  description: string | null;
  fileUrl: string | null;
};

export type ReconcileResponse = {
  year: number;
  ledger_total: number;
  w2_box1: number | null;
  reconciliation_status: 'MATCHED' | 'UNDER_REPORTED' | 'OVER_REPORTED';
  is_statutory_employee: boolean | null;
  total_business_expenses?: number;
  monthly_settlements: { month: number; total: number }[];
  settlement_details?: SettlementDetailItem[];
  w2_record: { employer_name?: string | null } | null;
};

export async function getW2List(): Promise<{ w2_records: W2Record[] }> {
  return apiRequest<{ w2_records: W2Record[] }>('/api/admin/w2/list');
}

export type W2UploadResult = {
  ok: boolean;
  id: string;
  year: number;
  wages_box1: number;
  tax_withheld_box2: number;
  is_statutory_employee: boolean;
  r2_key: string;
};

export async function uploadW2(file: File, year: number): Promise<W2UploadResult> {
  const url = `${API_BASE_URL}/api/admin/w2/upload`;
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const form = new FormData();
  form.append('file', file);
  form.append('year', String(year));
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { method: 'POST', body: form, headers });
  if (!response.ok) {
    const err = await parseJsonResponse<{ error?: string }>(response).catch(() => ({}));
    throw new Error(err?.error || `Upload failed: ${response.status}`);
  }
  return parseJsonResponse<W2UploadResult>(response);
}

export async function getReconcile(year: number): Promise<ReconcileResponse> {
  return apiRequest<ReconcileResponse>(`/api/admin/reconcile/${year}`);
}

/** Document download URL – use when expense has documentId for reliable PDF fetch */
export function getDocumentDownloadUrl(documentId: string): string {
  if (!documentId) return '';
  return `${API_BASE_URL}/api/documents/${documentId}/download`;
}

/** Fetch formatted receipt HTML for binder / View Receipt (travel email-body receipts). */
export async function getDocumentReceipt(documentId: string): Promise<string> {
  const url = `${API_BASE_URL}/api/documents/${documentId}/receipt`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const err = await parseJsonResponse<{ error?: string }>(response).catch(() => ({}));
    throw new Error((err as { error?: string })?.error || `Receipt failed: ${response.status}`);
  }
  return response.text();
}

/** Fetch document as Blob (for PDF preview embed in Review modal). */
export async function fetchDocumentBlob(documentId: string): Promise<Blob> {
  const url = `${API_BASE_URL}/api/documents/${documentId}/download`;
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const err = await parseJsonResponse<{ error?: string }>(response).catch(() => ({}));
    throw new Error((err as { error?: string })?.error || `Download failed: ${response.status}`);
  }
  return response.blob();
}

/** Download document via authenticated fetch; opens blob in new tab (for Loadboard View file / View attachment) */
export async function downloadDocument(documentId: string): Promise<void> {
  if (!documentId) return;
  const blob = await fetchDocumentBlob(documentId);
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
}

// =====================================================
// DATA MIGRATION HELPERS
// =====================================================

// Map cloud expense format to local format (handles fileUrl -> fileData conversion)
function mapCloudExpenseToLocal(cloudExpense: any): Expense {
  return {
    id: cloudExpense.id,
    category: cloudExpense.category,
    subcategory: cloudExpense.subcategory,
    description: cloudExpense.description,
    amount: cloudExpense.amount,
    date: cloudExpense.date,
    month: cloudExpense.month,
    year: cloudExpense.year,
    filename: cloudExpense.filename,
    fileData: cloudExpense.documentId ? getDocumentDownloadUrl(cloudExpense.documentId) : (cloudExpense.fileUrl ? getFileUrl(cloudExpense.fileUrl) : ''),
    fileUrl: cloudExpense.fileUrl,
    fileType: cloudExpense.fileType,
    fileSize: cloudExpense.fileSize,
    createdAt: cloudExpense.createdAt,
    freightBillNumber: cloudExpense.freightBillNumber,
    isSentToBennett: cloudExpense.isSentToBennett,
    provenanceSource: cloudExpense.source ?? 'manual',
    sourceRef: cloudExpense.sourceRef ?? undefined,
    parseStatus: cloudExpense.parseStatus ?? 'parsed',
    fileBacked: cloudExpense.fileBacked ?? true,
    mode: cloudExpense.mode === 'family' ? 'family' : 'business',
  };
}

// Export data as JSON backup (fetches from cloud)
export async function exportData(): Promise<string> {
  const expenses = await getAllExpenses();
  const settings = await getSettings();
  return JSON.stringify({ expenses, settings, exportDate: new Date().toISOString() }, null, 2);
}

// Import data from JSON backup (uploads to cloud)
export async function importData(jsonData: string): Promise<{ imported: number; errors: number }> {
  const data = JSON.parse(jsonData);
  let imported = 0;
  let errors = 0;

  if (data.expenses && Array.isArray(data.expenses)) {
    for (const expense of data.expenses) {
      try {
        await addExpense(expense);
        imported++;
      } catch {
        errors++;
      }
    }
  }

  if (data.settings) {
    await saveSettings(data.settings);
  }

  return { imported, errors };
}

// Health check
export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return response.ok;
  } catch {
    return false;
  }
}

